import type { PureFunction } from '@bonsai-components/utility-types';
import { ModButton } from '@moderneinc/design-system-components';
import {
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { signIn } from 'next-auth/react';
import {
  type FunctionComponent,
  type JSX,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import {
  type LoginScreenCustomization,
  SignInFooter
} from '../../components/authentication/signin/signin-footer.component';
import { BrandingImage } from '../../components/branding/branding-image.component';
import { ModerneMonogram } from '../../components/branding/moderne-monogram.component';
import { UnauthenticatedLayout } from '../../components/layout/unauthenticated-layout.component';
import { TENANT_BRANDING_ASPECT_RATIO } from '../../constants/general';
import {
  GENERIC_SIGN_IN_ERROR,
  KNOWN_SIGN_IN_ERRORS
} from '../../constants/messages';
import {
  type AuthProviderNames,
  ConfigurationContext
} from '../../contexts/config.context';
import { BitBucketIcon, GitHubIcon, GitLabIcon } from '../../icons/icons';

export type SignInOptions = {
  kc_idp_hint?: 'github' | 'bitbucket' | 'gitlab';
};

export type MultiTenantAuthProvider = {
  id: AuthProviderNames;
  name: string;
  idpHint?: SignInOptions['kc_idp_hint'];
  icon?: JSX.Element;
};

export const authProviderConfig: MultiTenantAuthProvider[] = [
  {
    id: 'github',
    name: 'GitHub',
    icon: <GitHubIcon fontSize="small" />
  },
  {
    id: 'bitbucket',
    name: 'Bitbucket Cloud',
    idpHint: 'bitbucket',
    icon: <BitBucketIcon fontSize="small" />
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    idpHint: 'gitlab',
    icon: <GitLabIcon fontSize="small" />
  },
  {
    id: 'sso',
    name: 'SSO'
  }
];

export const mapAuthProvider = (
  provider: AuthProviderNames
): MultiTenantAuthProvider => authProviderConfig.find((p) => p.id === provider);

export const getAuthProviderOptions: PureFunction<
  AuthProviderNames,
  Record<string, string>
> = (provider) => {
  const providerConfig = mapAuthProvider(provider);
  const options: Record<string, string> = {};
  if (providerConfig?.idpHint) {
    options.kc_idp_hint = providerConfig.idpHint;
  }
  return options;
};

const headingSx = {
  color: semanticColors.color.text.primary,
  textAlign: 'center',
  fontWeight: typography.fontWeight.medium,
  // Mock login h1: 19px/500.
  fontSize: '19px',
  lineHeight: 1.4,
  letterSpacing: '-0.01em'
} as const;

const customMessageSx = {
  color: semanticColors.color.text.primary,
  textAlign: 'center',
  fontWeight: typography.fontWeight.medium,
  fontSize: '18px',
  lineHeight: 1.4
} as const;

// Moderne-operated tenants render Moderne as the brand, so they skip the
// cobranded layout — they're always self-branded, never customer-branded.
const MODERNE_OPERATED_TENANTS = new Set(['app', 'dev', 'devaz']);

const SignIn: FunctionComponent = () => {
  const router = useRouter();
  const { config, getAuthProviders } = useContext(ConfigurationContext);

  const [requestedProvider, setRequestedProvider] =
    useState<AuthProviderNames>(null);
  const [loginCustomization, setLoginCustomization] =
    useState<LoginScreenCustomization>();

  const isModerneOperated = MODERNE_OPERATED_TENANTS.has(config?.name);
  const errorCode = router.query.error as string;
  const errorMessage = errorCode
    ? (KNOWN_SIGN_IN_ERRORS[errorCode] ?? GENERIC_SIGN_IN_ERROR)
    : null;
  const customText =
    !errorCode && loginCustomization?.text ? loginCustomization.text : null;
  const showWelcome = !errorMessage && !customText;
  const showTenantBrand = !isModerneOperated && Boolean(config?.branding);

  /**
   * Preserve hash fragment in callbackUrl when redirected to sign-in page.
   * Hash fragments are not sent to the server, so they remain on the signin page URL.
   * This effect moves the hash from the signin page to the callbackUrl parameter.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || !router.isReady) {
      return;
    }

    const hash = window.location.hash;
    if (hash && router.query.callbackUrl) {
      const currentCallbackUrl = router.query.callbackUrl as string;

      // Only update if the hash isn't already in the callbackUrl
      if (!currentCallbackUrl.includes(hash)) {
        const newCallbackUrl = currentCallbackUrl + hash;

        // Update the query params with the hash included in callbackUrl
        router.replace(
          {
            pathname: router.pathname,
            query: { ...router.query, callbackUrl: newCallbackUrl }
          },
          undefined,
          { shallow: true }
        );

        // Remove hash from the signin page URL
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );
      }
    }
  }, [router]);

  useEffect(() => {
    if (!config?.apiGatewayUrl) {
      return;
    }
    fetch(`${config.apiGatewayUrl}/login/customization`)
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
      })
      .then((data) => {
        if (data && (data.text || data.links)) {
          setLoginCustomization(data);
        }
      })
      .catch(() => {
        // Login customization is optional; ignore fetch failures
      });
  }, [config?.apiGatewayUrl]);

  const handleSignIn = useCallback(
    (providerId?: AuthProviderNames) => {
      // update button states
      setRequestedProvider(providerId);

      // get any provider options
      const providerOptions = providerId
        ? getAuthProviderOptions(providerId)
        : null;

      // sanitize the callback url
      const callbackUrlFromQuery = router.query.callbackUrl as string;
      let callbackUrl: string;
      try {
        // remove the `error` from the query params
        const parsedCallbackUrl = new URL(callbackUrlFromQuery);
        parsedCallbackUrl.searchParams.delete('error');
        parsedCallbackUrl.searchParams.delete('startAuth');
        callbackUrl = parsedCallbackUrl.toString();
      } catch {
        callbackUrl = callbackUrlFromQuery || '/';
      }

      return signIn(
        'keycloak',
        {
          // work around for infinite redirect issue
          // @see https://github.com/nextauthjs/next-auth/issues/4234
          callbackUrl,
          redirect: false
        },
        providerOptions ?? null
      ).then(() => setRequestedProvider(null));
    },
    [router.query.callbackUrl]
  );

  useEffect(() => {
    if (router?.query?.startAuth) {
      handleSignIn();
    }
  }, [handleSignIn, router.query?.startAuth]);

  return (
    <UnauthenticatedLayout>
      <Stack
        direction="column"
        sx={{
          gap: `${spacing.spacing_3}px`,
          alignItems: 'center',
          width: '100%',
          maxWidth: 340
        }}
      >
        {showWelcome && (
          <Typography component="h2" sx={headingSx}>
            Sign in to Moderne
          </Typography>
        )}
        {showTenantBrand && (
          <Box
            sx={{
              display: 'inline-flex',
              justifyContent: 'center',
              minHeight: 54,
              aspectRatio: TENANT_BRANDING_ASPECT_RATIO
            }}
          >
            <BrandingImage
              src={config.branding}
              darkSrc={config.brandingDark}
              alt={config.name}
              style={{
                alignSelf: 'center',
                maxWidth: 270,
                maxHeight: 68,
                minHeight: 30,
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
        {errorMessage && (
          <Typography component="h2" sx={headingSx}>
            {errorMessage}
          </Typography>
        )}
        {customText && (
          <Typography variant="body" sx={customMessageSx}>
            {customText}
          </Typography>
        )}
        <Stack
          sx={{
            gap: `${spacing.spacing_2}px`,
            width: '100%'
          }}
        >
          {getAuthProviders()
            .map(mapAuthProvider)
            .map((provider) => (
              <ModButton
                key={provider.name}
                data-testid={`login-button-${provider.name}`}
                startIcon={provider.icon}
                variant="primary"
                sx={{
                  // Morpheus login primary pill (mock .pill-btn--primary): 13px/600.
                  width: '100%',
                  height: 36,
                  padding: `${spacing.spacing_1}px ${spacing.spacing_2}px`,
                  fontSize: (theme) => theme.typography.pxToRem(13),
                  lineHeight: 1
                }}
                loading={provider?.id === requestedProvider}
                onClick={() => handleSignIn(provider.id)}
              >
                Sign in with {provider.name}
              </ModButton>
            ))}
        </Stack>
        <SignInFooter customization={loginCustomization} />
        <ModerneMonogram size={30} />
      </Stack>
    </UnauthenticatedLayout>
  );
};

export default SignIn;
